import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Alert, Platform, useWindowDimensions, Modal, TextInput } from 'react-native';
import { appConfig, getTwoCardMaxWidth, getCardMargin } from '../../config/configManager';
import { getColor } from '../../config/appConfig';
import { ConfigManager } from '../../config/configManager';
import { convertAppProfileToChildProfile } from '../../types/profile';
import { AccountSetupView } from './AccountSetupView';
import { DebugView } from './DebugView';
import { UpgradeView } from './UpgradeView';
import { useUserStore, useProfileStore } from '../../contexts/StoreContext';
import { syncDirtyProfiles } from '../../utils/syncBeforeSignOut';
import { AccountUserStorage } from '../../storage/AccountUserStorage';
import type { AccountUserDto, AccountUserPutDto, AccountReferFriendDto, AccountGetInfoResponseDto, AccountReferRewardDto, AccountReferCompleteDto, AccountReferCompleteResponseDto } from '../../types/api/service';

// Development credentials - inline for compatibility
const _proc = (globalThis as any).process;
const DEV_CREDENTIALS = {
  Email: _proc?.env?.TEST_USER_EMAIL || '',
  Password: _proc?.env?.TEST_USER_PASSWORD || '',
  Signature: 'testsignature123',
  AccessType: '5',
  SkinType: '1',
};

const isDevelopmentMode = (): boolean => {
  return typeof __DEV__ !== 'undefined' ? __DEV__ : ((globalThis as any).process?.env?.NODE_ENV === 'development');
};

interface AccountViewProps {
  storage?: any;
  apiService?: any;
  iapService?: any;
  contentService?: any;
  useContentStore?: any;
  initialSubView?: string;
}

// Utility function to normalize date formats for comparison
const normalizeDate = (dateString: string): Date => {
  // Handle server dates without timezone info (assume UTC)
  if (dateString && !dateString.includes('Z') && !dateString.includes('+')) {
    // Add 'Z' to indicate UTC for consistent parsing
    return new Date(dateString + 'Z');
  }
  return new Date(dateString);
};

// Check if sync is needed considering date formats and 2-minute threshold
const shouldSyncProfile = (serverModified: string, localSyncDate: string | undefined | null): boolean => {
  if (!localSyncDate) return true; // No local sync date means we should sync
  
  const serverDate = normalizeDate(serverModified);
  const localDate = normalizeDate(localSyncDate);
  
  // 2-minute threshold in milliseconds
  const SYNC_THRESHOLD_MS = 2 * 60 * 1000;
  const timeDifference = serverDate.getTime() - localDate.getTime();
  
  console.log(`   📅 Date comparison for profile:`);
  console.log(`     Server: ${serverModified} -> ${serverDate.toISOString()}`);
  console.log(`     Local:  ${localSyncDate} -> ${localDate.toISOString()}`);
  console.log(`     Diff:   ${Math.round(timeDifference / 1000)}s (threshold: 120s)`);
  
  return timeDifference > SYNC_THRESHOLD_MS;
};

export const AccountView: React.FC<AccountViewProps> = ({ storage, apiService, iapService, contentService, useContentStore, initialSubView }) => {
  const config = appConfig.getConfig();
  
  // Navigation state
  const [showAccountSetup, setShowAccountSetup] = useState(false);
  const [showDebugView, setShowDebugView] = useState(false);
  const [showFeaturesView, setShowFeaturesView] = useState(false);
  const [showUpgradeView, setShowUpgradeView] = useState(false);
  const [authStatus, setAuthStatus] = useState<string>('Checking...');
  const [authTiming, setAuthTiming] = useState<string>('');
  const [lastOperation, setLastOperation] = useState<string>('');
  const [accountEmail, setAccountEmail] = useState<string>('');
  const [accountDisplayName, setAccountDisplayName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [enableChangeAccount, setEnableChangeAccount] = useState(config.features.enableChangeAccount);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [accountUsers, setAccountUsers] = useState<AccountUserDto[]>([]);
  const [accountGetInfo, setAccountGetInfo] = useState<AccountGetInfoResponseDto | null>(null);
  const [showProgramDetails, setShowProgramDetails] = useState(false);
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [newUserName, setNewUserName] = useState('');
  const [addUserError, setAddUserError] = useState<string | null>(null);
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editUserName, setEditUserName] = useState('');
  const [editUserError, setEditUserError] = useState<string | null>(null);
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<AccountUserDto | null>(null);
  const [showReferForm, setShowReferForm] = useState(false);
  const [referName, setReferName] = useState('');
  const [referEmail, setReferEmail] = useState('');
  const [referError, setReferError] = useState<string | null>(null);
  const [isReferring, setIsReferring] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  
  // Access stores for authentication timing and profile data
  const userStore = useUserStore();
  const profileStore = useProfileStore();
  const profileState = profileStore(); // Get the state once at component level
  const { getActiveProfile } = profileState;
  const { width: screenWidth } = useWindowDimensions();
  const isWide = screenWidth >= 700;
  
  // Helper function to calculate minutes since a timestamp
  const getMinutesSince = (timestamp: string): number => {
    const now = new Date().getTime();
    const then = new Date(timestamp).getTime();
    return Math.floor((now - then) / (1000 * 60));
  };

  // Helper function to get sync status information
  const getSyncStatus = () => {
    const dirtyProfiles = profileState.profiles.filter((p: any) => p.isDirty);
    const authFailures = profileState.profiles.filter((p: any) => 
      p.syncFailureReason && (
        p.syncFailureReason.includes('Session expired') ||
        p.syncFailureReason.includes('Authentication') ||
        p.syncFailureReason.includes('Auth retry') ||
        p.syncFailureReason.includes('Invalid or missing JWT token')
      )
    );
    
    if (authFailures.length > 0) {
      return {
        status: `Authentication issue - ${authFailures.length} profile${authFailures.length !== 1 ? 's' : ''} need sync`,
        icon: '⚠️',
        isError: true
      };
    } else if (dirtyProfiles.length > 0) {
      return {
        status: `${dirtyProfiles.length} profile${dirtyProfiles.length !== 1 ? 's' : ''} pending sync`,
        icon: '🔄',
        isError: false
      };
    } else {
      return {
        status: 'Signed in - data backed up and synchronized',
        icon: '☁️',
        isError: false
      };
    }
  };

  const handleAddUser = useCallback(async () => {
    const trimmedName = newUserName.trim();
    if (!trimmedName) {
      setAddUserError('Name is required.');
      return;
    }
    const nameExists = accountUsers.some(
      (u) => u.Name.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (nameExists) {
      setAddUserError(`'${trimmedName}' already exists. Please choose a unique name.`);
      return;
    }
    setAddUserError(null);
    setIsAddingUser(true);
    try {
      const payload: AccountUserPutDto = {
        UserId: '0',
        Name: trimmedName,
        Status: 'active',
      };
      const request = {
        RequestType: 'account_put_user',
        Payload: JSON.stringify(payload),
      };
      const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
        userStore,
        storage,
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Backend returned an error');
      }
      let newUser: AccountUserDto;
      if (typeof response.payload === 'string') {
        newUser = JSON.parse(response.payload);
      } else {
        newUser = response.payload as AccountUserDto;
      }
      // Persist the new user into the local AccountGetInfo store
      if (storage) {
        const userState = userStore && typeof userStore !== 'boolean' && (userStore as any).getState
          ? (userStore as any).getState()
          : null;
        const accountId = userState?.session?.user?.familyId || userState?.session?.familyAccount?.familyId || '';
        if (accountId) {
          const accountUserStorage = new AccountUserStorage(storage);
          const dto = await accountUserStorage.getAccountGetInfo(accountId);
          if (dto) {
            const updated = { ...dto, accountusers: [...(dto.accountusers ?? []), newUser] };
            await accountUserStorage.saveAccountGetInfo(accountId, updated);
          }
        }
      }
      setAccountUsers((prev) => [...prev, newUser]);
      setNewUserName('');
      setShowAddUserForm(false);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      setAddUserError(`Failed to add user: ${msg}`);
    } finally {
      setIsAddingUser(false);
    }
  }, [newUserName, accountUsers, apiService, userStore, storage]);

  const handleSaveUser = useCallback(async (user: AccountUserDto) => {
    const trimmedName = editUserName.trim();
    if (!trimmedName) {
      setEditUserError('Name is required.');
      return;
    }
    const nameExists = accountUsers.some(
      (u) => u.UserId !== user.UserId && u.Name.trim().toLowerCase() === trimmedName.toLowerCase()
    );
    if (nameExists) {
      setEditUserError(`'${trimmedName}' already exists. Please choose a unique name.`);
      return;
    }
    setEditUserError(null);
    setIsSavingUser(true);
    try {
      const payload: AccountUserPutDto = { UserId: user.UserId, Name: trimmedName, Status: user.Status };
      const request = {
        RequestType: 'account_put_user',
        Payload: JSON.stringify(payload),
      };
      const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
        userStore,
        storage,
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Backend returned an error');
      }
      let updatedUser: AccountUserDto;
      if (typeof response.payload === 'string') {
        updatedUser = JSON.parse(response.payload);
      } else {
        updatedUser = response.payload as AccountUserDto;
      }
      if (storage) {
        const userState = userStore && typeof userStore !== 'boolean' && (userStore as any).getState
          ? (userStore as any).getState()
          : null;
        const accountId = userState?.session?.user?.familyId || userState?.session?.familyAccount?.familyId || '';
        if (accountId) {
          const accountUserStorage = new AccountUserStorage(storage);
          const dto = await accountUserStorage.getAccountGetInfo(accountId);
          if (dto) {
            const updated = {
              ...dto,
              accountusers: (dto.accountusers ?? []).map((u) =>
                u.UserId === updatedUser.UserId ? updatedUser : u
              ),
            };
            await accountUserStorage.saveAccountGetInfo(accountId, updated);
          }
        }
      }
      setAccountUsers((prev) => prev.map((u) => u.UserId === updatedUser.UserId ? updatedUser : u));
      setEditingUserId(null);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      setEditUserError(`Failed to save: ${msg}`);
    } finally {
      setIsSavingUser(false);
    }
  }, [editUserName, accountUsers, apiService, userStore, storage]);

  const handleSendRefer = useCallback(async () => {
    const trimmedName = referName.trim();
    const trimmedEmail = referEmail.trim();
    if (!trimmedName) {
      setReferError('Name is required.');
      return;
    }
    if (!trimmedEmail) {
      setReferError('Email is required.');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setReferError('Please enter a valid email address.');
      return;
    }
    setReferError(null);
    setIsReferring(true);
    try {
      const payload: AccountReferFriendDto = {
        Name: trimmedName,
        Email: trimmedEmail,
        Status: '0',
      };
      const request = {
        RequestType: 'account_put_refer',
        Payload: JSON.stringify(payload),
      };
      const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
        userStore,
        storage,
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Backend returned an error');
      }
      let newRefer: AccountReferFriendDto;
      if (typeof response.payload === 'string') {
        newRefer = JSON.parse(response.payload);
      } else {
        newRefer = response.payload as AccountReferFriendDto;
      }
      // Persist into local AccountGetInfo store
      if (storage) {
        const userState = userStore && typeof userStore !== 'boolean' && (userStore as any).getState
          ? (userStore as any).getState()
          : null;
        const accountId = userState?.session?.user?.familyId || userState?.session?.familyAccount?.familyId || '';
        if (accountId) {
          const accountUserStorage = new AccountUserStorage(storage);
          const dto = await accountUserStorage.getAccountGetInfo(accountId);
          if (dto) {
            const updated = { ...dto, refers: [...(dto.refers ?? []), newRefer] };
            await accountUserStorage.saveAccountGetInfo(accountId, updated);
            setAccountGetInfo(updated);
          }
        }
      }
      setReferName('');
      setReferEmail('');
      setShowReferForm(false);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      setReferError(`Failed to send referral: ${msg}`);
    } finally {
      setIsReferring(false);
    }
  }, [referName, referEmail, apiService, userStore, storage]);

  const handleDeleteUser = useCallback((user: AccountUserDto) => {
    setDeleteConfirmUser(user);
  }, []);

  const confirmDeleteUser = useCallback(async () => {
    const user = deleteConfirmUser;
    if (!user) return;
    setDeleteConfirmUser(null);
    setIsSavingUser(true);
    try {
      const payload: AccountUserPutDto = { UserId: user.UserId, Name: user.Name, Status: 'Delete' };
      const request = {
        RequestType: 'account_put_user',
        Payload: JSON.stringify(payload),
      };
      const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
        userStore,
        storage,
      });
      if (!response?.success) {
        throw new Error(response?.message || 'Backend returned an error');
      }
      if (storage) {
        const userState = userStore && typeof userStore !== 'boolean' && (userStore as any).getState
          ? (userStore as any).getState()
          : null;
        const accountId = userState?.session?.user?.familyId || userState?.session?.familyAccount?.familyId || '';
        if (accountId) {
          const accountUserStorage = new AccountUserStorage(storage);
          const dto = await accountUserStorage.getAccountGetInfo(accountId);
          if (dto) {
            const updated = {
              ...dto,
              accountusers: (dto.accountusers ?? []).filter((u) => u.UserId !== user.UserId),
            };
            await accountUserStorage.saveAccountGetInfo(accountId, updated);
          }
        }
      }
      setAccountUsers((prev) => prev.filter((u) => u.UserId !== user.UserId));
      setEditingUserId(null);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      setEditUserError(`Failed to delete user: ${msg}`);
    } finally {
      setIsSavingUser(false);
    }
  }, [deleteConfirmUser, apiService, userStore, storage]);

  // Load account users from storage (via AccountGetInfo)
  const loadAccountUsers = useCallback(async () => {
    if (!storage) return;
    try {
      const userState = userStore && typeof userStore !== 'boolean' && (userStore as any).getState
        ? (userStore as any).getState()
        : null;
      const accountId = userState?.session?.user?.familyId || userState?.session?.familyAccount?.familyId || '';
      if (!accountId) return;
      const accountUserStorage = new AccountUserStorage(storage);
      const dto = await accountUserStorage.getAccountGetInfo(accountId);
      setAccountUsers(dto?.accountusers ?? []);
      setAccountGetInfo(dto ?? null);
    } catch (error) {
      console.warn('AccountView: Could not load account users:', error);
    }
  }, [storage, userStore]);

  // Check authentication status on mount and load persisted debug settings
  useEffect(() => {
    checkAuthStatus();
    loadAccountUsers();

    // Handle return from Stripe cancel: re-open the upgrade view automatically
    if ((globalThis as any).__upgradeReturnCancel) {
      delete (globalThis as any).__upgradeReturnCancel;
      setShowUpgradeView(true);
    }

    // Load persisted enableChangeAccount debug setting
    const loadDebugSettings = async () => {
      if (storage) {
        try {
          const changeAccountSetting = await storage.getItem('debug_enable_change_account');
          if (changeAccountSetting === 'true') {
            setEnableChangeAccount(true);
          }
        } catch (error) {
          console.warn('AccountView: Could not load debug settings:', error);
        }
      }
    };
    loadDebugSettings();
  }, []);

  // Referral qualification check: fires whenever accountGetInfo or profiles change.
  // Conditions: ReferredBy is set, Qualified is not yet set, at least one profile has
  // milestones AND concerns initially updated, and all within the first 30 days.
  useEffect(() => {
    console.log('🎯 AccountView: Referral qualification effect triggered', {
      ReferredBy: accountGetInfo?.ReferredBy || '(not set)',
      Qualified:  accountGetInfo?.Qualified  || '(not set)',
      profileCount: profileState.profiles?.length ?? 0,
      hasApiService: !!apiService,
      hasStorage:    !!storage,
    });

    if (!accountGetInfo?.ReferredBy) {
      console.log('🎯 AccountView: Referral check — skipping: no ReferredBy');
      return;
    }
    if (accountGetInfo?.Qualified) {
      console.log('🎯 AccountView: Referral check — skipping: already Qualified =', accountGetInfo.Qualified);
      return;
    }
    if (!apiService || !storage) {
      console.log('🎯 AccountView: Referral check — skipping: apiService or storage unavailable');
      return;
    }

    const checkReferralQualification = async () => {
      // Use profileState.profiles from the closure — this is exactly what triggered the effect
      // and reflects the current reactive state without needing an extra getState() call.
      const profiles: any[] = profileState.profiles ?? [];
      console.log('🎯 AccountView: Referral check — profiles count:', profiles.length);

      // Condition 1: at least one profile has milestones recorded
      const milestoneCounts = profiles.map((p: any) => ({ id: p.id, count: p.milestonestate?.length ?? 0 }));
      const hasMilestones = profiles.some((p: any) => (p.milestonestate?.length ?? 0) > 0);
      console.log('🎯 AccountView: Referral check — milestone counts per profile:', milestoneCounts, '| hasMilestones:', hasMilestones);

      // Condition 2: at least one profile has concerns recorded
      const concernCounts = profiles.map((p: any) => ({ id: p.id, count: p.concernsstate?.length ?? 0 }));
      const hasConcerns   = profiles.some((p: any) => (p.concernsstate?.length ?? 0) > 0);
      console.log('🎯 AccountView: Referral check — concern counts per profile:', concernCounts, '| hasConcerns:', hasConcerns);

      if (!hasMilestones || !hasConcerns) {
        console.log('🎯 AccountView: Referral check — skipping: conditions not met (hasMilestones:', hasMilestones, ', hasConcerns:', hasConcerns, ')');
        return;
      }

      // Condition 3: account was created within the last 30 days.
      // Use accountGetInfo.Created (from account_get_info API) — this is the authoritative
      // server-side date. userSession.user.createdDate can be C# DateTime.MinValue (0001-01-01)
      // when createdAt was not set on the login response.
      const userState = userStore?.getState?.();
      const accountId = userState?.session?.user?.familyId || userState?.session?.familyAccount?.familyId || '';
      const createdDate = accountGetInfo.Created;
      const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
      const accountAgeMs = createdDate ? Date.now() - new Date(createdDate).getTime() : null;
      const accountAgeDays = accountAgeMs !== null ? Math.round(accountAgeMs / (1000 * 60 * 60 * 24)) : null;
      console.log('🎯 AccountView: Referral check — Created:', createdDate, '| ageDays:', accountAgeDays, '| accountId:', accountId);

      if (!createdDate) {
        console.log('🎯 AccountView: Referral check — skipping: Created not available in AccountGetInfo');
        return;
      }
      if (accountAgeMs! > THIRTY_DAYS_MS) {
        console.log('🎯 AccountView: Referral check — skipping: account too old (', accountAgeDays, 'days > 30)');
        return;
      }

      // All conditions met — notify backend
      console.log('🎯 AccountView: Referral check — ALL conditions met, calling account_refer_complete', {
        ReferredBy: accountGetInfo.ReferredBy, accountId,
      });
      try {
        const payload: AccountReferCompleteDto = { ReferredBy: accountGetInfo.ReferredBy };
        const request = {
          RequestType: 'account_refer_complete',
          Payload: JSON.stringify(payload),
        };
        const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
          userStore,
          storage,
        });
        console.log('🎯 AccountView: Referral check — API response:', { success: response?.success, payload: response?.payload });
        if (response?.success) {
          let responseDto: AccountReferCompleteResponseDto;
          if (typeof response.payload === 'string') {
            responseDto = JSON.parse(response.payload);
          } else {
            responseDto = response.payload as AccountReferCompleteResponseDto;
          }
          // Latch the qualification locally so this never fires again
          if (accountId) {
            const accountUserStorage = new AccountUserStorage(storage);
            const dto = await accountUserStorage.getAccountGetInfo(accountId);
            if (dto) {
              const updated = { ...dto, Qualified: responseDto?.Qualified || '1' };
              await accountUserStorage.saveAccountGetInfo(accountId, updated);
              setAccountGetInfo(updated);
              console.log('🎯 AccountView: Referral check — Qualified latched:', updated.Qualified);
            }
          }
        }
      } catch (error) {
        console.warn('🎯 AccountView: Referral qualification check failed:', error);
      }
    };

    checkReferralQualification();
  }, [accountGetInfo, profileState.profiles]);


  // Handle navigation to account setup
  const handleAccountSetup = () => {
    setShowAccountSetup(true);
  };

  const handleSignOut = () => {
    setShowSignOutModal(true);
  };

  const handleDeleteAccount = async () => {
    if (!apiService) {
      Alert.alert('Error', 'API service not available.');
      return;
    }
    setIsDeletingAccount(true);
    try {
      const familyId = userStore?.getState?.()?.session?.user?.familyId;
      const request = {
        RequestType: 'account_put_delete',
        Payload: JSON.stringify({ accountId: familyId }),
      };
      const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
        userStore,
        storage,
      });
      // API returns Success (pascal) or success (camel) depending on serializer
      const succeeded = response?.success === true || (response as any)?.Success === true;
      if (succeeded) {
        // Full sign-out: wipes IndexedDB, localStorage, resets all stores.
        // __webSignOut is App.tsx handleSignOut — it handles everything including logout().
        if (Platform.OS === 'web' && (globalThis as any).__webSignOut) {
          await (globalThis as any).__webSignOut();
        } else {
          // Non-web fallback
          if (userStore?.getState) {
            await userStore.getState().logout();
          }
        }
      } else {
        setIsDeletingAccount(false);
        setShowDeleteAccountModal(false);
        Alert.alert('Delete Failed', response?.message || 'Could not delete account. Please try again.');
      }
    } catch (err: any) {
      setIsDeletingAccount(false);
      setShowDeleteAccountModal(false);
      Alert.alert('Delete Failed', err?.message || 'An unexpected error occurred.');
    }
  };

  const confirmSignOut = () => {
    setShowSignOutModal(false);
    const doSignOut = async () => {
      // Sync any locally-modified profiles before clearing the session
      if (Platform.OS === 'web') {
        await syncDirtyProfiles();
      }
      // Clear Zustand user session + localStorage auth keys
      if (userStore?.getState) {
        await userStore.getState().logout();
      }
      // On web, signal App.tsx to show the sign-in page
      if (Platform.OS === 'web') {
        (globalThis as any).__webSignOut?.();
      }
    };

    doSignOut();
  };

  const handleBackFromAccount = () => {
    setShowAccountSetup(false);
    checkAuthStatus(); // Refresh status when returning
  };

  // Handle navigation to debug view
  const handleSyncData = () => {
    setShowDebugView(true);
  };

  const handleBackFromDebug = () => {
    setShowDebugView(false);
  };

  // Handle navigation to features view
  const handleShowFeatures = () => {
    setShowFeaturesView(true);
  };


  const checkAuthStatus = useCallback(async () => {
    console.log('🔧 AccountView: checkAuthStatus called');
    if (!storage) {
      setAuthStatus('Storage not available');
      setAuthTiming('');
      return;
    }

    try {
      const storedEmail = await storage.getItem('user_email');
      const storedName = await storage.getItem('user_name');
      const storedToken = await storage.getItem('auth_token');
      console.log('🔧 AccountView: Storage items - email:', !!storedEmail, 'name:', !!storedName, 'token:', !!storedToken);
      
      // Get authentication timing from user store and fallback methods
      let timingInfo = '';
      try {
        console.log('🔧 AccountView: userStore available:', !!userStore);
        
        // Try to get timing from user store first
        if (userStore && userStore.getState) {
          const state = userStore.getState();
          const session = state?.session;
          console.log('🔧 AccountView: session available:', !!session, 'authenticated:', session?.isAuthenticated);
          
          if (session && session.isAuthenticated && session.sessionStartTime) {
            const loginMinutes = getMinutesSince(session.sessionStartTime);
            const activityMinutes = session.lastActivityTime ? getMinutesSince(session.lastActivityTime) : null;
            
            if (activityMinutes !== null) {
              timingInfo = `Login: ${loginMinutes}m ago, Activity: ${activityMinutes}m ago`;
            } else {
              timingInfo = `Login: ${loginMinutes}m ago`;
            }
            console.log('🔧 AccountView: Generated timing from session:', timingInfo);
          }
        }
        
        // Fallback: Try to get timing from stored auth timestamp
        if (!timingInfo && storedToken) {
          try {
            const authTimestamp = await storage.getItem('auth_timestamp');
            if (authTimestamp) {
              const authMinutes = getMinutesSince(authTimestamp);
              timingInfo = `Token: ${authMinutes}m ago`;
              console.log('🔧 AccountView: Generated timing from stored timestamp:', timingInfo);
            } else {
              // Create timestamp for future reference
              const now = new Date().toISOString();
              await storage.setItem('auth_timestamp', now);
              timingInfo = 'Token: 0m ago (just set)';
              console.log('🔧 AccountView: Created new auth timestamp');
            }
          } catch (timestampError) {
            console.warn('🔧 AccountView: Could not access auth timestamp:', timestampError);
          }
        }
        
      } catch (userStoreError) {
        console.error('🔧 AccountView: Error accessing user store:', userStoreError);
        timingInfo = 'Error accessing timing info';
      }
      
      if (storedEmail && storedToken) {
        const devMode = isDevelopmentMode() && storedEmail === DEV_CREDENTIALS.Email;
        setAuthStatus(devMode ? 'Development Mode' : 'Active');
        setAuthTiming(timingInfo || 'Timing info not available');
        
        // Store account info for rendering
        const accountInfo = storedName ? `${storedName} (${storedEmail})` : storedEmail;
        setLastOperation(accountInfo); // Reuse this state for account display
        setAccountEmail(storedEmail);
        setAccountDisplayName(storedName || '');
      } else if (storedEmail) {
        setAuthStatus(`Saved: ${storedEmail} (needs refresh)`);
        setAuthTiming('');
      } else {
        const devMode = isDevelopmentMode();
        setAuthStatus(devMode ? 'Development mode (offline)' : 'Running in offline mode');
        setAuthTiming('');
      }
      console.log('🔧 AccountView: Final auth status:', authStatus, 'timing:', timingInfo);
    } catch (error) {
      console.error('🔧 AccountView: Error in checkAuthStatus:', error);
      setAuthStatus('Error checking status');
      setAuthTiming('');
    }
  }, [storage, userStore]);

  const handleReLogin = async () => {
    if (!apiService || !storage) {
      Alert.alert('Error', 'API service or storage not available');
      return;
    }

    setIsLoading(true);
    setLastOperation('Attempting re-login...');
    
    try {
      console.log('🔐 AccountView: Attempting authentication with credentials:', {
        Email: DEV_CREDENTIALS.Email,
        Password: '[HIDDEN]',
        Signature: DEV_CREDENTIALS.Signature,
        AccessType: DEV_CREDENTIALS.AccessType,
        SkinType: DEV_CREDENTIALS.SkinType
      });
      
      // Create login request object - apiService.authenticate expects a single object parameter
      const loginRequest = {
        Email: DEV_CREDENTIALS.Email,
        Password: DEV_CREDENTIALS.Password,
        Signature: DEV_CREDENTIALS.Signature,
        AccessType: DEV_CREDENTIALS.AccessType,
        SkinType: DEV_CREDENTIALS.SkinType
      };
      
      const loginResponse = await apiService.authenticate(loginRequest);
      
      if (loginResponse?.success && loginResponse.data?.token) {
        console.log('📝 AccountView: Login Response UserInfoDto Details:');
        console.log('   ✨ User Info Object:', JSON.stringify(loginResponse.data.user, null, 2));
        console.log('   🆔 Account ID:', loginResponse.data.user?.accountId || 'N/A');
        console.log('   🆔 SRA ID:', loginResponse.data.user?.sraId || 'N/A');
        console.log('   📛 Name:', loginResponse.data.user?.name || 'N/A');
        console.log('   📧 Email:', loginResponse.data.user?.email || 'N/A');
        console.log('   📱 Phone:', loginResponse.data.user?.phone || 'N/A');
        console.log('   ❤️ Preferred:', loginResponse.data.user?.preferred || 'N/A');
        console.log('   🔐 Two Factor Enabled:', loginResponse.data.user?.twoFactorEnabled || 'N/A');
        console.log('   ✅ Is Active:', loginResponse.data.user?.isActive || 'N/A');
        console.log('   📅 Created At:', loginResponse.data.user?.createdAt || 'N/A');
        console.log('   ✉️ Email Confirmed:', loginResponse.data.user?.emailConfirmed || 'N/A');
        console.log('   ✍️ Valid Signature:', loginResponse.data.user?.validSignature || 'N/A');
        
        // Check for profile sync data in login response
        console.log('🔄 AccountView: Profile Sync Check:');
        const serverProfiles = loginResponse.data?.profiles?.profiles || [];
        console.log('   📋 Server profiles count:', serverProfiles.length);
        console.log('   📋 Server profiles:', serverProfiles.map((p: any) => ({ id: p.profileId, modified: p.modified })));
        
        console.log('💾 AccountView: Storage Operations:');
        console.log('   🗄️ Storing user_email:', DEV_CREDENTIALS.Email);
        console.log('   🎟️ Storing auth_token: [TOKEN_SET]');
        console.log('   ⏱️ Storing auth_timestamp:', new Date().toISOString());
        // Store credentials and timestamp
        await storage.setItem('user_email', DEV_CREDENTIALS.Email);
        await storage.setItem('auth_token', loginResponse.data.token);
        await storage.setItem('auth_timestamp', new Date().toISOString());
        
        // Update API service
        apiService.setAuthToken(loginResponse.data.token);
        
        // Perform profile sync if we have server profiles and profile store
        if (serverProfiles.length > 0 && profileStore) {
          try {
            console.log('🔄 AccountView: Starting comprehensive profile sync process...');
            const profileStoreState = profileStore.getState();
            const localProfiles = profileStoreState.profiles;
            const accountId = loginResponse.data.user?.accountId || 'default-account';
            
            // Determine which profiles need syncing
            const profilesToSync = [];
            let syncedCount = 0;
            let addedCount = 0;
            let updatedCount = 0;
            
            for (const serverProfile of serverProfiles) {
              const localProfile = localProfiles.find((p: any) => p.id === serverProfile.profileId);
              
              if (!localProfile) {
                console.log(`   ➕ New profile found: ${serverProfile.profileId}`);
                profilesToSync.push({ profileId: serverProfile.profileId, reason: 'NEW' });
                addedCount++;
              } else {
                if (shouldSyncProfile(serverProfile.modified, localProfile.lastSyncDate)) {
                  console.log(`   🔄 Profile needs update: ${serverProfile.profileId}`);
                  profilesToSync.push({ profileId: serverProfile.profileId, reason: 'UPDATE' });
                  updatedCount++;
                } else {
                  console.log(`   ✅ Profile up to date: ${serverProfile.profileId}`);
                }
              }
              syncedCount++;
            }
            
            // Perform actual sync operations
            if (profilesToSync.length > 0) {
              console.log(`🔄 AccountView: Syncing ${profilesToSync.length} profiles...`);
              
              let syncResults = { successful: 0, failed: 0, errors: [] as string[] };
              
              for (const profileToSync of profilesToSync) {
                try {
                  console.log(`   📥 Fetching profile: ${profileToSync.profileId}`);
                  
                  // Create profile_get request
                  const request = {
                    RequestType: 'profile_get',
                    Payload: JSON.stringify({ profileId: profileToSync.profileId, accountId })
                  };
                  
                  // Fetch profile from server with automatic 401 retry handling
                  const response = await apiService.processServiceRequestWithRefresh(request, 'sync', {
                    userStore,
                    storage,
                  });
                  
                  if (response?.success) {
                    // Parse the response payload (same fix as DebugView)
                    let appProfileDto;
                    try {
                      if (typeof response.payload === 'string') {
                        appProfileDto = JSON.parse(response.payload);
                      } else {
                        appProfileDto = response.payload;
                      }
                    } catch (parseError) {
                      throw new Error(`Failed to parse profile data: ${parseError instanceof Error ? parseError.message : String(parseError)}`);
                    }
                    
                    console.log(`   ✅ Fetched profile data for ${profileToSync.profileId}`);
                    
                    // Store in AppProfileStorage
                    try {
                      const { AppProfileStorage } = await import('../../services/AppProfileStorage');
                      const appProfileStorage = new AppProfileStorage(storage);
                      await appProfileStorage.saveProfile(appProfileDto);
                      console.log(`   💾 Stored AppProfile data for ${profileToSync.profileId}`);
                    } catch (appStorageError) {
                      console.warn(`   ⚠️ AppProfileStorage failed for ${profileToSync.profileId}:`, appStorageError);
                    }
                    
                    // Check if this is a new profile or an update
                    const existingProfile = localProfiles.find((p: any) => p.id === profileToSync.profileId);
                    if (existingProfile) {
                      const profileModified = appProfileDto.modified ?? appProfileDto.Modified ?? new Date().toISOString();
                      // Update existing profile
                      profileStoreState.updateProfile(profileToSync.profileId, {
                        lastSyncDate: profileModified,
                        isDirty: false,
                        syncFailureReason: undefined
                      });
                      console.log(`   🔄 Updated existing profile sync data: ${profileToSync.profileId}`);
                    } else {
                      // Add new profile - convert AppProfileDto to ChildProfile format
                      console.log(`   ➕ Adding new profile with server ID: ${profileToSync.profileId}`);
                      
                      try {
                        // Convert AppProfileDto to ChildProfile (reuse DebugView logic)
                        const childProfile = convertAppProfileToChildProfile(appProfileDto, storage);
                        
                        // Ensure we preserve the server's profile ID
                        if (typeof profileStoreState.addProfile === 'function') {
                          // skipBackendSync=true: profile came FROM the server, no need to upload it back
                          await profileStoreState.addProfile(childProfile, { skipBackendSync: true });
                          
                          // Verify the profile was added with correct ID
                          const addedProfile = profileStoreState.profiles.find((p: any) => p.id === profileToSync.profileId);
                          if (addedProfile) {
                            console.log(`   ✅ Successfully added profile with ID: ${profileToSync.profileId}`);
                          } else {
                            console.warn(`   ⚠️ Profile was added but ID mismatch detected. Expected: ${profileToSync.profileId}`);
                            // Try to find and fix the profile that was just added
                            const latestProfile = profileStoreState.profiles[profileStoreState.profiles.length - 1];
                            if (latestProfile && latestProfile.name === childProfile.name) {
                              console.log(`   🔧 Attempting to fix profile ID from ${latestProfile.id} to ${profileToSync.profileId}`);
                              profileStoreState.updateProfile(latestProfile.id, { ...latestProfile, id: profileToSync.profileId });
                            }
                          }
                        } else {
                          console.error(`   ❌ addProfile method not available on profile store`);
                        }
                      } catch (conversionError) {
                        console.error(`   ❌ Failed to convert AppProfileDto to ChildProfile:`, conversionError);
                        throw conversionError;
                      }
                    }
                    
                    syncResults.successful++;
                  } else {
                    throw new Error(`Server error: ${response?.message || 'Unknown error'}`);
                  }
                } catch (error) {
                  const errorMessage = error instanceof Error ? error.message : String(error);
                  console.error(`   ❌ Failed to sync ${profileToSync.profileId}:`, errorMessage);
                  syncResults.failed++;
                  syncResults.errors.push(`${profileToSync.profileId}: ${errorMessage}`);
                }
              }
              
              console.log(`🔄 AccountView: Sync completed - Success: ${syncResults.successful}, Failed: ${syncResults.failed}`);
              if (syncResults.errors.length > 0) {
                console.log('   ❌ Sync Errors:', syncResults.errors);
              }
              
              setLastOperation(`✅ Re-login successful (${syncResults.successful}/${profilesToSync.length} profiles synced)`);
            } else {
              console.log('🔄 AccountView: All profiles are up to date');
              setLastOperation(`✅ Re-login successful (${syncedCount} profiles up to date)`);
            }
            
            console.log(`🔄 AccountView: Profile sync summary - Checked: ${syncedCount}, New: ${addedCount}, Updates: ${updatedCount}`);
          } catch (profileSyncError) {
            console.error('🔄 AccountView: Profile sync error:', profileSyncError);
            setLastOperation('✅ Re-login successful (profile sync failed)');
          }
        } else {
          console.log('🔄 AccountView: No server profiles or profile store not available');
          setLastOperation('✅ Re-login successful');
        }
        
        Alert.alert('Success', 'Re-login completed successfully');
        
        // Refresh auth status after a brief delay
        setTimeout(() => {
          checkAuthStatus();
        }, 500);
      } else {
        setLastOperation('❌ Re-login failed');
        Alert.alert('Error', 'Re-login failed');
      }
    } catch (error) {
      console.error('🔐 AccountView: Re-login error:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      setLastOperation(`❌ Re-login error: ${errorMessage}`);
      
      // Check if it's an authentication-specific error
      if (errorMessage.includes('400') || errorMessage.includes('validation')) {
        Alert.alert('Authentication Error', 'Invalid credentials or request format. Please check the development credentials.');
      } else {
        Alert.alert('Error', `Re-login failed: ${errorMessage}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // If showing account setup, render that view instead
  if (showAccountSetup) {
    return (
      <AccountSetupView 
        onBack={handleBackFromAccount}
        storage={storage}
        apiService={apiService}
      />
    );
  }


  // If showing features view, render that view instead
  // Render helpers
  if (showUpgradeView) {
    return <UpgradeView onBack={() => setShowUpgradeView(false)} apiService={apiService} iapService={iapService} storage={storage} />;
  }
  const renderAccountSection = (title: string, children: React.ReactNode, extraStyle?: object, headerRight?: React.ReactNode) => (
    <View style={[styles.section, extraStyle]}>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingRight: 20 }}>
        <Text style={styles.sectionTitle}>{title}</Text>
        {headerRight}
      </View>
      {children}
    </View>
  );

  const renderActionItem = (
    title: string,
    description: string,
    onPress: () => void,
    icon?: string,
    disabled?: boolean
  ) => (
    <TouchableOpacity 
      style={[styles.actionItem, disabled && styles.actionItemDisabled]} 
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
    >
      <View style={styles.actionContent}>
        {icon && <Text style={styles.actionIcon}>{icon}</Text>}
        <View style={styles.actionText}>
          <Text style={[styles.actionTitle, disabled && styles.actionTitleDisabled]}>{title}</Text>
          <Text style={[styles.actionDescription, disabled && styles.actionDescriptionDisabled]}>{description}</Text>
        </View>
      </View>
      <Text style={[styles.actionArrow, disabled && styles.actionArrowDisabled]}>›</Text>
    </TouchableOpacity>
  );

  const renderInfoItem = (title: string, value: string, icon?: string) => (
    <View style={styles.infoItem}>
      {icon && <Text style={styles.actionIcon}>{icon}</Text>}
      <View style={styles.actionText}>
        <Text style={styles.actionTitle}>{title}</Text>
        <Text style={styles.infoValue}>{value}</Text>
      </View>
    </View>
  );

  // Determine if user has account
  const hasAccount = authStatus.includes('Active') || authStatus.includes('Development Mode');
  const isPremiumAccount = appConfig.isPremium();

  return (
    <View style={styles.backgroundContainer}>
      <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
        <View style={[styles.header, { maxWidth: getTwoCardMaxWidth(), alignSelf: 'center', width: '100%' }]}>
          <Text style={styles.title}>Account</Text>
          <Text style={styles.subtitle}>Manage your account and family access</Text>
        </View>
        <View style={{ maxWidth: getTwoCardMaxWidth(), alignSelf: 'center', width: '100%', paddingHorizontal: getCardMargin() }}>
        {/* Two-column layout: col1 = My Account + Family Access, col2 = Refer A Friend */}
        <View style={{ flexDirection: isWide ? 'row' : 'column', gap: 16, alignItems: isWide ? 'flex-start' : 'stretch' }}>
        {/* Column 1: My Account + Family Access */}
        <View style={{ flex: isWide ? 1 : undefined }}>
        {/* Section 1: My Account */}
        {renderAccountSection(
          '🧑 My Account',
          (
            <>
              {!hasAccount ? (
                <>
                  {renderActionItem(
                    'Setup Your Account',
                    'Sign in or create account for data backup and sync',
                    handleAccountSetup,
                    '�'
                  )}
                  {renderInfoItem(
                    'Current Status',
                    'Running in offline mode - data stored locally only',
                    '📱'
                  )}
                </>
              ) : (
                <>
                  {renderInfoItem(
                    'Active Account',
                    lastOperation || authStatus,
                    '✅'
                  )}
                  {enableChangeAccount && renderActionItem(
                    'Change Account',
                    'Sign into a different existing Account',
                    () => setShowAccountSetup(true),
                    '🔐'
                  )}
                  {renderActionItem(
                    'Account Features',
                    'Learn more about account types and app features',
                    handleShowFeatures,
                    isPremiumAccount ? '🏆' : '📋'
                  )}
                  {!isPremiumAccount && renderActionItem(
                    'Upgrade to Premium',
                    hasAccount ? 'Unlock advanced milestones, expanded access, and more' : 'Account required to upgrade to Premium',
                    () => setShowUpgradeView(true),
                    '⭐',
                    !hasAccount
                  )}
                  {Platform.OS === 'web' && renderActionItem(
                    'Sign Out',
                    'Sign out and return to the sign-in screen',
                    handleSignOut,
                    '🚪'
                  )}
                </>
              )}
            </>
          ),
          { marginHorizontal: 0, marginTop: 0 },
          hasAccount ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              {accountGetInfo?.RefCode ? (
                <Text style={{ fontSize: 12, color: '#718096', fontWeight: '500' }}>
                  Code: {accountGetInfo.RefCode}
                </Text>
              ) : null}
              <TouchableOpacity onPress={() => setShowDeleteAccountModal(true)}>
                <Text style={{ fontSize: 12, color: '#e53e3e' }}>Delete</Text>
              </TouchableOpacity>
            </View>
          ) : undefined
        )}

        {renderAccountSection('👨‍👩‍👧‍👦 Family Access Sign Ins', (
          <>
            {hasAccount ? (
              accountUsers.length > 0 ? (
                <View style={{ paddingHorizontal: 16, paddingTop: 10, paddingBottom: 4 }}>
                  {accountUsers.map((user) => (
                  <View key={user.UserId} style={styles.userCard}>
                    {editingUserId === user.UserId ? (
                      <View style={styles.addUserForm}>
                        <TextInput
                          style={[styles.addUserInput, editUserError ? styles.addUserInputError : null]}
                          placeholder="Name"
                          placeholderTextColor="#aaa"
                          value={editUserName}
                          onChangeText={(t) => { setEditUserName(t); setEditUserError(null); }}
                          editable={!isSavingUser}
                          autoFocus
                        />
                        {editUserError ? (
                          <Text style={styles.addUserErrorText}>{editUserError}</Text>
                        ) : null}
                        <View style={styles.editUserButtons}>
                          {accountUsers.length > 1 && !(accountDisplayName && user.Name.trim().toLowerCase() === accountDisplayName.trim().toLowerCase()) && (
                            <TouchableOpacity
                              style={[styles.addUserBtn, styles.addUserBtnDanger]}
                              onPress={() => handleDeleteUser(user)}
                              disabled={isSavingUser}
                            >
                              <Text style={styles.addUserBtnDangerText}>Delete</Text>
                            </TouchableOpacity>
                          )}
                          <View style={styles.editUserButtonsRight}>
                            <TouchableOpacity
                              style={[styles.addUserBtn, styles.addUserBtnSecondary]}
                              onPress={() => { setEditingUserId(null); setEditUserName(''); setEditUserError(null); }}
                              disabled={isSavingUser}
                            >
                              <Text style={styles.addUserBtnSecondaryText}>Cancel</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={[styles.addUserBtn, styles.addUserBtnPrimary, isSavingUser && styles.addUserBtnDisabled]}
                              onPress={() => handleSaveUser(user)}
                              disabled={isSavingUser}
                            >
                              <Text style={styles.addUserBtnPrimaryText}>{isSavingUser ? 'Saving...' : 'Save'}</Text>
                            </TouchableOpacity>
                          </View>
                        </View>
                      </View>
                    ) : (
                      <View style={styles.userCardRow}>
                        <Text style={styles.actionIcon}>✅</Text>
                        <View style={styles.actionText}>
                          <Text style={styles.actionTitle}>
                            {user.Name}{accountDisplayName && user.Name.trim().toLowerCase() === accountDisplayName.trim().toLowerCase() ? ' *' : ''}
                          </Text>
                          <Text style={styles.infoValue}>
                            {accountEmail ? `${user.Name} (${accountEmail})` : 'Account member'}
                          </Text>
                        </View>
                        <TouchableOpacity
                          onPress={() => { setEditingUserId(user.UserId); setEditUserName(user.Name); setEditUserError(null); }}
                        >
                          <Text style={styles.editLink}>Edit</Text>
                        </TouchableOpacity>
                      </View>
                    )}
                  </View>
                  ))}
                </View>
              ) : (
                renderInfoItem('Connected Devices', 'No account users found — sync account data to load', '📟')
              )
            ) : (
              renderInfoItem('Connected Devices', 'Sign in to view connected devices', '📟')
            )}
            {renderActionItem(
              'Add Family Member Or Device',
              !hasAccount
                ? 'Account required to add family members or devices'
                : !isPremiumAccount
                  ? 'Premium required to add family members or devices'
                  : 'Add another parent or caregiver, or connect a new device',
              () => { setShowAddUserForm((v) => !v); setNewUserName(''); setAddUserError(null); },
              '🧑',
              !hasAccount || !isPremiumAccount
            )}
            {showAddUserForm && hasAccount && isPremiumAccount && (
              <View style={styles.addUserForm}>
                <TextInput
                  style={[styles.addUserInput, addUserError ? styles.addUserInputError : null]}
                  placeholder="Name"
                  placeholderTextColor="#aaa"
                  value={newUserName}
                  onChangeText={(t) => { setNewUserName(t); setAddUserError(null); }}
                  editable={!isAddingUser}
                  autoFocus
                />
                {addUserError ? (
                  <Text style={styles.addUserErrorText}>{addUserError}</Text>
                ) : null}
                <View style={styles.addUserButtons}>
                  <TouchableOpacity
                    style={[styles.addUserBtn, styles.addUserBtnSecondary]}
                    onPress={() => { setShowAddUserForm(false); setNewUserName(''); setAddUserError(null); }}
                    disabled={isAddingUser}
                  >
                    <Text style={styles.addUserBtnSecondaryText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.addUserBtn, styles.addUserBtnPrimary, isAddingUser && styles.addUserBtnDisabled]}
                    onPress={handleAddUser}
                    disabled={isAddingUser}
                  >
                    <Text style={styles.addUserBtnPrimaryText}>{isAddingUser ? 'Adding...' : 'Add'}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}

          </>
        ), { marginHorizontal: 0, marginTop: 16 })}
        </View>
        {/* Column 2: Refer A Friend */}
        <View style={{ flex: isWide ? 1 : undefined }}>
        {renderAccountSection('🎁 Refer A Friend', (
          <>
            {/* ── Your Referral List ── */}
            {renderInfoItem(
              'Your Referral List',
              (() => {
                const refers = accountGetInfo?.refers;
                if (!refers || refers.length === 0) return 'No active referrals — start sharing to earn rewards!';
                return `${refers.length} referral${refers.length !== 1 ? 's' : ''}`;
              })(),
              '🧑'
            )}
            {(() => {
              const refers = accountGetInfo?.refers;
              if (!refers || refers.length === 0) return null;
              return (
                <View style={{ paddingHorizontal: 30, paddingBottom: 4 }}>
                  {refers.map((r: AccountReferFriendDto, i: number) => {
                    const statusLabel = r.Status === '1' ? 'Started' : r.Status === '2' ? 'Confirmed' : 'Pending';
                    const statusColor = r.Status === '2' ? '#059669' : r.Status === '1' ? '#2563eb' : '#888';
                    const statusIcon = r.Status === '2' ? '\u2705' : r.Status === '1' ? '\ud83d\udd35' : '\u23f3';
                    return (
                      <View key={i} style={[styles.referDescription, { marginLeft: 16 }]}>
                        <Text style={{ fontSize: 16, marginRight: 8 }}>{statusIcon}</Text>
                        <Text style={{ flex: 1, fontSize: 13, color: '#1a1a2e' }} numberOfLines={1}>
                          {r.Name} <Text style={{ color: '#555e6b' }}>({r.Email})</Text>
                        </Text>
                        <Text style={{ fontSize: 12, color: statusColor, marginLeft: 8 }}>{statusLabel}</Text>
                      </View>
                    );
                  })}
                </View>
              );
            })()}

            {/* ── Invite A Friend ── */}
            {renderActionItem(
              'Invite A Friend',
              hasAccount ? 'Refer Child Nav and earn rewards for successful referrals' : 'Account required to send referrals',
              () => { setShowReferForm((v) => !v); setReferName(''); setReferEmail(''); setReferError(null); },
              '📤',
              !hasAccount
            )}
            {showReferForm && hasAccount && (
              <View style={styles.addUserForm}>
                <TextInput
                  style={[styles.addUserInput, referError ? styles.addUserInputError : null]}
                  placeholder="Name"
                  placeholderTextColor="#aaa"
                  value={referName}
                  onChangeText={(t) => { setReferName(t); setReferError(null); }}
                  editable={!isReferring}
                  autoFocus
                />
                <TextInput
                  style={[styles.addUserInput, referError ? styles.addUserInputError : null]}
                  placeholder="Email"
                  placeholderTextColor="#aaa"
                  value={referEmail}
                  onChangeText={(t) => { setReferEmail(t); setReferError(null); }}
                  editable={!isReferring}
                  keyboardType="email-address"
                  autoCapitalize="none"
                />
                {referError ? (
                  <Text style={styles.addUserErrorText}>{referError}</Text>
                ) : null}
                <View style={styles.addUserButtons}>
                  <TouchableOpacity
                    style={[styles.addUserBtn, styles.addUserBtnSecondary]}
                    onPress={() => { setShowReferForm(false); setReferName(''); setReferEmail(''); setReferError(null); }}
                    disabled={isReferring}
                  >
                    <Text style={styles.addUserBtnSecondaryText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.addUserBtn, styles.addUserBtnPrimary, isReferring && styles.addUserBtnDisabled]}
                    onPress={handleSendRefer}
                    disabled={isReferring}
                  >
                    <Text style={styles.addUserBtnPrimaryText}>{isReferring ? 'Sending...' : 'Send'}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {/* ── Gift Rewards Earned ── */}
            {renderInfoItem(
              'Gift Rewards Earned',
              (() => {
                const rewards = accountGetInfo?.rewards;
                if (!rewards || rewards.length === 0) return 'No rewards earned yet';
                const unredeemed = rewards.filter((r: AccountReferRewardDto) => !r.Redeemed || r.Redeemed === '0');
                return unredeemed.length > 0
                  ? `${unredeemed.length} unredeemed reward${unredeemed.length !== 1 ? 's' : ''} (${rewards.length} total)`
                  : `${rewards.length} reward${rewards.length !== 1 ? 's' : ''} earned (all redeemed)`;
              })(),
              '🎁'
            )}
            {(() => {
              const rewards = accountGetInfo?.rewards;
              const unredeemed = (rewards ?? []).filter((r: AccountReferRewardDto) => !r.Redeemed || r.Redeemed === '0');
              if (unredeemed.length === 0) return null;
              return (
                <View style={{ paddingHorizontal: 16, paddingBottom: 4 }}>
                  {unredeemed.map((r: AccountReferRewardDto, i: number) => (
                    <View key={i} style={[styles.referDescription, { marginLeft: 30, flexDirection: 'column', alignItems: 'stretch' }]}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                        <Text style={{ fontSize: 13, fontWeight: '600', color: '#1a1a2e', flex: 1 }}>🎟️ Gift Reward Code</Text>
                        <TouchableOpacity
                          onPress={() => {
                            if (Platform.OS === 'web') {
                              (navigator as any)?.clipboard?.writeText?.(r.Code);
                            } else {
                              Alert.alert('Reward Code', r.Code);
                            }
                          }}
                          style={{ paddingHorizontal: 8, paddingVertical: 2, backgroundColor: '#e8f0fe', borderRadius: 4 }}
                        >
                          <Text style={{ fontSize: 11, color: '#2563eb', fontWeight: '600' }}>📋 Copy</Text>
                        </TouchableOpacity>
                      </View>
                      <Text selectable style={{ fontSize: 15, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined, letterSpacing: 1, color: '#1a1a2e', marginBottom: 4 }}>
                        {r.Code}
                      </Text>
                      <View style={{ flexDirection: 'row', gap: 12 }}>
                        {r.Value ? <Text style={{ fontSize: 12, color: '#555e6b' }}>Value: {r.Value}</Text> : null}
                        {r.Expires ? <Text style={{ fontSize: 12, color: '#888' }}>Expires: {r.Expires}</Text> : null}
                      </View>
                    </View>
                  ))}
                </View>
              );
            })()}

            {renderActionItem(
              'Program Details',
              'Learn how to earn rewards through referrals',
              () => setShowProgramDetails(true),
              'ℹ️'
            )}
          </>
        ), { marginHorizontal: 0, marginTop: 0 })}
        </View>
        </View>
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            {hasAccount 
              ? 'Your data is automatically backed up and synced across your Family Access Sign Ins.'
              : 'Create an account to backup data and share access with family members.'}
          </Text>
        </View>
        </View>
      </ScrollView>

      {/* Delete user confirmation modal */}
      <Modal
        visible={!!deleteConfirmUser}
        transparent
        animationType="fade"
        onRequestClose={() => setDeleteConfirmUser(null)}
      >
        <View style={styles.toggleUserModalBackdrop}>
          <View style={styles.toggleUserModalCard}>
            <Text style={styles.toggleUserModalTitle}>Delete User</Text>
            <Text style={styles.toggleUserModalMessage}>
              {'Delete '}
              <Text style={{ fontWeight: '700' }}>{deleteConfirmUser?.Name ?? ''}</Text>
              {'\n\nThis user will be permanently removed from your account. This cannot be undone.'}
            </Text>
            <View style={styles.toggleUserModalButtons}>
              <TouchableOpacity
                style={styles.toggleUserBtnSecondary}
                onPress={() => setDeleteConfirmUser(null)}
              >
                <Text style={styles.toggleUserBtnSecondaryText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.toggleUserBtnDanger}
                onPress={confirmDeleteUser}
              >
                <Text style={styles.toggleUserBtnDangerText}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Account Features Modal */}
      <Modal
        visible={showFeaturesView}
        transparent
        animationType="fade"
        onRequestClose={() => setShowFeaturesView(false)}
      >
        <View style={styles.signOutDialogBackdrop}>
          <View style={[styles.signOutDialogCard, { maxHeight: '85%' }]}>
            <Text style={styles.signOutDialogTitle}>Account Features</Text>
            <ScrollView showsVerticalScrollIndicator={true} style={{ marginBottom: 12 }}>
              <Text style={styles.signOutDialogMessage}>
                Compare and discover ChildNav's capabilities.
              </Text>
              {/* Table header */}
              <View style={{ flexDirection: 'row', marginBottom: 4, paddingHorizontal: 4 }}>
                <View style={{ flex: 1 }}><Text style={{ fontWeight: '700', fontSize: 12, color: '#1a1a2e' }}>Feature</Text></View>
                <View style={{ width: 64, alignItems: 'center' }}><Text style={{ fontWeight: '700', fontSize: 12, color: '#555e6b' }}>Standard</Text></View>
                <View style={{ width: 64, alignItems: 'center' }}><Text style={{ fontWeight: '700', fontSize: 12, color: '#7c3aed' }}>Premium</Text></View>
              </View>
              {([
                { feature: 'Core CDC Milestones',             standard: true,  premium: true  },
                { feature: 'Standard Profiles (2)',          standard: true,  premium: true  },
                { feature: 'Development Tracking',          standard: true,  premium: true  },
                { feature: 'Concerns & Actions Tracking',     standard: true,  premium: true  },
                { feature: 'Essential Assessments',       standard: true,  premium: true  },
                { feature: 'Extended CDC Milestones',   standard: false, premium: true  },
                { feature: 'ASQ® Equivalent Domains',            standard: false, premium: true  },
                { feature: 'Advanced Analytics',          standard: false, premium: true  },
                { feature: 'Family Access (Unlimited)',  standard: false, premium: true  },
                { feature: 'Expanded Profiles (10)',                 standard: false, premium: true  },
                { feature: 'Expanded Activities',            standard: false, premium: true  },
                { feature: 'Expanded Assessments',            standard: false, premium: true  },
              ] as { feature: string; standard: boolean; premium: boolean }[]).map((item, idx) => (
                <View key={idx} style={{ flexDirection: 'row', paddingVertical: 7, paddingHorizontal: 4, borderTopWidth: 1, borderTopColor: '#f1f2f6', alignItems: 'center' }}>
                  <Text style={{ flex: 1, fontSize: 13, color: '#1a1a2e' }}>{item.feature}</Text>
                  <View style={{ width: 64, alignItems: 'center' }}>
                    <Text style={{ fontSize: 15, color: item.standard ? '#10b981' : '#d1d5db' }}>{item.standard ? '✓' : '✗'}</Text>
                  </View>
                  <View style={{ width: 64, alignItems: 'center' }}>
                    <Text style={{ fontSize: 15, color: item.premium ? '#10b981' : '#d1d5db' }}>{item.premium ? '✓' : '✗'}</Text>
                  </View>
                </View>
              ))}
              <Text style={{ fontSize: 13, color: '#555e6b', textAlign: 'center', marginTop: 12 }}>
                {'You currently have: '}
                <Text style={{ fontWeight: '700', color: isPremiumAccount ? '#7c3aed' : '#1a1a2e' }}>
                  {isPremiumAccount ? 'Premium' : 'Standard'}
                </Text>
              </Text>
            </ScrollView>
            <View style={styles.signOutDialogButtons}>
              <TouchableOpacity
                style={[styles.signOutDialogBtnPrimary, { backgroundColor: '#10b981' }]}
                onPress={() => setShowFeaturesView(false)}
              >
                <Text style={styles.signOutDialogBtnPrimaryText}>Got It</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Program Details Modal */}
      <Modal
        visible={showProgramDetails}
        transparent
        animationType="fade"
        onRequestClose={() => setShowProgramDetails(false)}
      >
        <View style={styles.signOutDialogBackdrop}>
          <View style={[styles.signOutDialogCard, { maxHeight: '85%' }]}>
            <Text style={styles.signOutDialogTitle}>Child Nav Referral Reward Program</Text>
            <ScrollView showsVerticalScrollIndicator={true} style={{ marginBottom: 12 }}>
              <Text style={styles.signOutDialogMessage}>
                Earn transferable gift codes redeemable toward a lifetime Premium purchase on any platform. Rewards are issued as you hit cumulative confirmed referral milestones — not additive per tier.
              </Text>
              <Text style={{ fontSize: 14, fontWeight: '600', color: '#10b981', textAlign: 'center', marginBottom: 10 }}>
                {accountGetInfo?.ReferCnt
                  ? `You have ${accountGetInfo.ReferCnt} confirmed referral${Number(accountGetInfo.ReferCnt) !== 1 ? 's' : ''}`
                  : 'You have 0 confirmed referrals'}
              </Text>

              {/* Tier 1 */}
              {(() => { const active = accountGetInfo?.ReferTier === '1'; return (
              <View style={{ borderWidth: active ? 2 : 1, borderColor: active ? '#10b981' : '#e2e8f0', borderRadius: 8, padding: 12, marginBottom: 8, backgroundColor: active ? '#f0fdf4' : undefined }}>
                <Text style={{ fontWeight: '700', color: active ? '#059669' : '#1a1a2e', fontSize: 14, marginBottom: 4 }}>Tier 1 — 1 referral{active ? '  ★' : ''}</Text>
                <Text style={{ color: '#374151', fontSize: 13 }}>
                  <Text style={{ fontWeight: '600' }}>You earn: </Text>$6.99 gift code toward lifetime Premium
                </Text>
              </View>
              ); })()}
              {/* Tier 2 */}
              {(() => { const active = accountGetInfo?.ReferTier === '2'; return (
              <View style={{ borderWidth: active ? 2 : 1, borderColor: active ? '#10b981' : '#e2e8f0', borderRadius: 8, padding: 12, marginBottom: 8, backgroundColor: active ? '#f0fdf4' : undefined }}>
                <Text style={{ fontWeight: '700', color: active ? '#059669' : '#1a1a2e', fontSize: 14, marginBottom: 4 }}>Tier 2 — 3 referrals (cumulative){active ? '  ★' : ''}</Text>
                <Text style={{ color: '#374151', fontSize: 13 }}>
                  <Text style={{ fontWeight: '600' }}>You earn: </Text>$12.99 gift code toward lifetime Premium
                </Text>
              </View>
              ); })()}
              {/* Tier 3 */}
              {(() => { const active = accountGetInfo?.ReferTier === '3'; return (
              <View style={{ borderWidth: active ? 2 : 1, borderColor: active ? '#10b981' : '#e2e8f0', borderRadius: 8, padding: 12, marginBottom: 8, backgroundColor: active ? '#f0fdf4' : undefined }}>
                <Text style={{ fontWeight: '700', color: active ? '#059669' : '#1a1a2e', fontSize: 14, marginBottom: 4 }}>Tier 3 — 5 referrals (cumulative){active ? '  ★' : ''}</Text>
                <Text style={{ color: '#374151', fontSize: 13 }}>
                  <Text style={{ fontWeight: '600' }}>You earn: </Text>$19.99 gift code (full lifetime Premium)
                </Text>
                <Text style={{ color: '#374151', fontSize: 13, marginTop: 2 }}>
                  <Text style={{ fontWeight: '600' }}>Referrals earn: </Text>$6.99 gift code to each of the 5 referrals
                </Text>
              </View>
              ); })()}

              <Text style={[styles.signOutDialogMessage, { marginTop: 4 }]}>
                <Text style={{ fontWeight: '600' }}>Important: </Text>
                Tiers are cumulative totals, not additional referrals per tier. A higher-tier code supersedes the previous tier's code — if the earlier code has not yet been redeemed it is invalidated when the new tier is reached.                
              </Text>
            </ScrollView>
            <View style={styles.signOutDialogButtons}>
              <TouchableOpacity
                style={[styles.signOutDialogBtnPrimary, { backgroundColor: '#10b981' }]}
                onPress={() => setShowProgramDetails(false)}
              >
                <Text style={styles.signOutDialogBtnPrimaryText}>Got It</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Delete Account Confirmation Modal */}
      <Modal
        visible={showDeleteAccountModal}
        transparent
        animationType="fade"
        onRequestClose={() => !isDeletingAccount && setShowDeleteAccountModal(false)}
      >
        <View style={styles.signOutDialogBackdrop}>
          <View style={styles.signOutDialogCard}>
            <Text style={[styles.signOutDialogTitle, { color: '#c53030' }]}>Delete Account</Text>
            <Text style={styles.signOutDialogMessage}>
              This will permanently delete your entire account and all associated data. This action cannot be undone.
            </Text>
            <Text style={[styles.signOutDialogMessage, { marginTop: 8, fontWeight: '600' }]}>
              You will be signed out immediately.
            </Text>
            <View style={styles.signOutDialogButtons}>
              <TouchableOpacity
                style={[styles.signOutDialogBtnPrimary, { backgroundColor: '#c53030' }, isDeletingAccount && { opacity: 0.6 }]}
                onPress={handleDeleteAccount}
                disabled={isDeletingAccount}
              >
                <Text style={styles.signOutDialogBtnPrimaryText}>
                  {isDeletingAccount ? 'Deleting…' : 'Delete Account'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.signOutDialogBtnSecondary}
                onPress={() => setShowDeleteAccountModal(false)}
                disabled={isDeletingAccount}
              >
                <Text style={styles.signOutDialogBtnSecondaryText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Sign Out Confirmation Modal */}
      <Modal
        visible={showSignOutModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowSignOutModal(false)}
      >
        <View style={styles.signOutDialogBackdrop}>
          <View style={styles.signOutDialogCard}>
            <Text style={styles.signOutDialogTitle}>Sign Out</Text>
            <Text style={styles.signOutDialogMessage}>
              Are you sure you want to sign out? Your local data will be preserved.
            </Text>
            <View style={styles.signOutDialogButtons}>
              <TouchableOpacity
                style={styles.signOutDialogBtnPrimary}
                onPress={confirmSignOut}
              >
                <Text style={styles.signOutDialogBtnPrimaryText}>Sign Out</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.signOutDialogBtnSecondary}
                onPress={() => setShowSignOutModal(false)}
              >
                <Text style={styles.signOutDialogBtnSecondaryText}>Cancel</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

    </View>
  );
};

const styles = StyleSheet.create({
  backgroundContainer: {
    flex: 1,
    backgroundColor: '#3b82f6',
  },
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  contentContainer: {
    backgroundColor: '#3b82f6',
    paddingVertical: 16,
  },
  header: {
    backgroundColor: 'transparent',
    padding: 24,
    borderBottomWidth: 0,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  subtitle: {
    fontSize: 16,
    color: '#e2e8f0',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  section: {
    backgroundColor: '#ffffff',
    marginHorizontal: 16,
    marginTop: 16,
    paddingVertical: 16,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2c3e50',
    paddingHorizontal: 20,
    marginBottom: 12,
  },
  actionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f2f6',
  },
  actionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  actionIcon: {
    fontSize: 20,
    marginRight: 12,
    width: 24,
    textAlign: 'center',
  },
  actionText: {
    flex: 1,
  },
  actionTitle: {
    fontSize: 16,
    fontWeight: '500',
    color: '#2c3e50',
    marginBottom: 2,
  },
  actionDescription: {
    fontSize: 14,
    color: '#7f8c8d',
  },
  actionArrow: {
    fontSize: 18,
    color: '#bdc3c7',
    fontWeight: 'bold',
  },
  actionItemDisabled: {
    opacity: 0.5,
  },
  actionTitleDisabled: {
    color: '#95a5a6',
  },
  actionDescriptionDisabled: {
    color: '#bdc3c7',
  },
  actionArrowDisabled: {
    color: '#ecf0f1',
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f2f6',
  },
  infoValue: {
    fontSize: 14,
    color: '#3498db',
    fontWeight: '500',
  },
  footer: {
    padding: 20,
    alignItems: 'center',
    marginBottom: 20,
  },
  footerText: {
    fontSize: 14,
    color: '#e2e8f0',
    textAlign: 'center',
    lineHeight: 20,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },

  // Sign Out confirmation dialog
  signOutDialogBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  signOutDialogCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 420,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 12,
    elevation: 8,
  },
  signOutDialogTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1a1a2e',
    marginBottom: 10,
  },
  signOutDialogMessage: {
    fontSize: 14,
    color: '#555',
    lineHeight: 20,
    marginBottom: 24,
  },
  signOutDialogButtons: {
    flexDirection: 'column',
    alignItems: 'stretch',
    marginTop: 4,
  },
  signOutDialogBtnSecondary: {
    paddingVertical: 12,
    marginHorizontal: 8,
    marginTop: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#3498db',
    alignItems: 'center',
  },
  signOutDialogBtnSecondaryText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#3498db',
    textAlign: 'center',
  },
  signOutDialogBtnPrimary: {
    paddingVertical: 12,
    marginHorizontal: 8,
    borderRadius: 8,
    backgroundColor: '#e74c3c',
    alignItems: 'center',
  },
  signOutDialogBtnPrimaryText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
  },

  // Add user inline form
  addUserForm: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f2f6',
    gap: 8,
  },
  addUserInput: {
    borderWidth: 1,
    borderColor: '#cbd5e0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    color: '#1a1a2e',
    backgroundColor: '#f8fafc',
  },
  addUserInputError: {
    borderColor: '#e74c3c',
  },
  addUserErrorText: {
    fontSize: 13,
    color: '#e74c3c',
  },
  addUserButtons: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'flex-end',
    marginTop: 4,
  },
  addUserBtn: {
    paddingVertical: 9,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
  },
  addUserBtnPrimary: {
    backgroundColor: '#3498db',
  },
  addUserBtnPrimaryText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  addUserBtnSecondary: {
    borderWidth: 1,
    borderColor: '#3498db',
  },
  addUserBtnSecondaryText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#3498db',
  },
  addUserBtnDisabled: {
    opacity: 0.5,
  },
  addUserBtnDanger: {
    borderWidth: 1,
    borderColor: '#e74c3c',
  },
  addUserBtnDangerText: {
    fontSize: 14,
    fontWeight: '600' as const,
    color: '#e74c3c',
  },
  addUserBtnSuccess: {
    borderWidth: 1,
    borderColor: '#27ae60',
  },
  addUserBtnSuccessText: {
    fontSize: 14,
    fontWeight: '600' as const,
    color: '#27ae60',
  },

  // Edit user button row (Disable/Enable left, Cancel+Save right)
  editUserButtons: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
    marginTop: 4,
  },
  editUserButtonsRight: {
    flexDirection: 'row' as const,
    gap: 8,
    flex: 1,
    justifyContent: 'flex-end' as const,
  },

  // Toggle user status confirmation modal
  toggleUserModalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  toggleUserModalCard: {
    backgroundColor: '#ffffff',
    borderRadius: 14,
    padding: 24,
    width: '100%',
    maxWidth: 420,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 8,
  },
  toggleUserModalTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#2c3e50',
    marginBottom: 12,
  },
  toggleUserModalMessage: {
    fontSize: 14,
    color: '#555e6b',
    lineHeight: 20,
    marginBottom: 24,
  },
  toggleUserModalButtons: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  toggleUserBtnSecondary: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d0d7de',
    backgroundColor: '#f8f9fa',
    alignItems: 'center',
  },
  toggleUserBtnSecondaryText: {
    fontSize: 13,
    color: '#555e6b',
    fontWeight: '500',
  },
  toggleUserBtnDanger: {
    paddingVertical: 9,
    paddingHorizontal: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e74c3c',
    backgroundColor: '#fdf2f2',
    alignItems: 'center',
  },
  toggleUserBtnDangerText: {
    fontSize: 13,
    color: '#e74c3c',
    fontWeight: '600',
  },
  toggleUserBtnSuccess: {
    paddingVertical: 9,
    paddingHorizontal: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#27ae60',
    backgroundColor: '#f0faf4',
    alignItems: 'center',
  },
  toggleUserBtnSuccessText: {
    fontSize: 13,
    color: '#27ae60',
    fontWeight: '600',
  },

  // Per-user cards in Family Access
  userCard: {
    backgroundColor: '#f8f9ff',
    borderWidth: 1,
    borderColor: '#dde1f0',
    borderRadius: 8,
    marginBottom: 6,
    overflow: 'hidden',
  },
  userCardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  editLink: {
    fontSize: 14,
    color: '#3498db',
    fontWeight: '500',
    paddingLeft: 12,
  },
  referDescription: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 6,
    backgroundColor: '#f8f9ff',
    borderWidth: 1,
    borderColor: '#dde1f0',
    borderRadius: 8,
  },
});